import React, { useState, useEffect, useContext, useMemo } from 'react';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { toast } from 'react-toastify';

import { makeStyles } from '@material-ui/core/styles';
import { green } from '@material-ui/core/colors';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';

import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import LinkIcon from '@material-ui/icons/Link';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import AddIcon from '@material-ui/icons/Add';
import LaunchIcon from '@material-ui/icons/Launch';

import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';
import ConfirmationModal from '../ConfirmationModal';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  formContainer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  fileList: {
    maxHeight: 400,
    overflow: 'auto',
  },
  btnWrapper: {
    position: 'relative',
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  editFields: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    width: '100%',
  },
  listItem: {
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  linkButton: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  linkPreview: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    wordBreak: 'break-all',
    marginTop: theme.spacing(0.5),
  },
  addForm: {
    '& .MuiTextField-root': {
      marginBottom: theme.spacing(2),
    },
  },
}));

const getFileLinkSchema = () =>
  Yup.object().shape({
    fileName: Yup.string()
      .min(2, i18n.t('fileLinksModal.validation.fileNameTooShort'))
      .required(i18n.t('fileLinksModal.validation.fileNameRequired')),
    fileUrl: Yup.string().required(
      i18n.t('fileLinksModal.validation.fileUrlRequired')
    ),
  });

const FileLinksModal = ({ open, onClose, ticket, reload, dealId }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const initialFormState = {
    fileName: '',
    fileUrl: '',
  };

  const [fileLinks, setFileLinks] = useState([]);
  const [editingLink, setEditingLink] = useState(null);
  const [editData, setEditData] = useState({ fileName: '', fileUrl: '' });
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  // Obtém o dealId considerando as várias possibilidades - priorizar deal ativo (ticket pode ter vários deals, apenas um ativo)
  const resolvedDealId = useMemo(() => {
    // 1. Se foi informado como prop, priorize dealId (ex.: StageActivitiesModal passa o deal ativo)
    if (dealId) return dealId;

    // 2. Buscar o deal ativo em ticket.deals (aberto + mais recente por id)
    if (Array.isArray(ticket?.deals) && ticket.deals.length > 0) {
      if (ticket.status === 'open') {
        const openDeals = ticket.deals.filter(deal => deal.status === 'open');
        if (openDeals.length > 0) {
          const latestOpen = openDeals.reduce((latest, current) =>
            (current?.id || 0) > (latest?.id || 0) ? current : latest
          );
          if (latestOpen?.id) return latestOpen.id;
        }
      }
      const latestDeal = ticket.deals.reduce((latest, current) =>
        (current?.id || 0) > (latest?.id || 0) ? current : latest
      );
      if (latestDeal?.id) return latestDeal.id;
    }

    // 3. Fallback: ticket.dealId ou ticket.deal.id
    if (ticket?.dealId) return ticket.dealId;
    if (ticket?.deal?.id) return ticket.deal.id;

    return null;
  }, [dealId, ticket]);

  // Carregar links do ticket
  useEffect(() => {
    if (!open) {
      return;
    }

    if (!resolvedDealId) {
      console.warn('DealId não encontrado ao carregar links. Ticket:', ticket);
      setFileLinks([]);
      return;
    }

    loadFileLinks(resolvedDealId);
  }, [open, resolvedDealId, ticket]);

  const loadFileLinks = async dealId => {
    if (!dealId) {
      console.warn('dealId não encontrado para carregar arquivos:', ticket);
      setFileLinks([]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.get(`/deals/${dealId}/files`);
      // Garantir que fileLinks seja sempre um array
      setFileLinks(Array.isArray(data.files) ? data.files : []);
    } catch (err) {
      console.error('Erro ao carregar arquivos:', err);
      toastError(err);
      setFileLinks([]); // Em caso de erro, definir como array vazio
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setEditingLink(null);
    setEditData({ fileName: '', fileUrl: '' });
    setFileLinks([]);
  };

  const handleAddFileLink = async (values, actions) => {
    try {
      if (!resolvedDealId) {
        toast.error('Não foi possível localizar o negócio para salvar o anexo. Verifique se o ticket possui um negócio associado.');
        actions.setSubmitting(false);
        return;
      }

      const linkData = {
        name: values.fileName.trim(),
        url: values.fileUrl.trim(),
      };

      await api.post(`/deals/${resolvedDealId}/files`, linkData);
      toast.success(i18n.t('fileLinksModal.toasts.linkAdded'));

      actions.resetForm();
      loadFileLinks(resolvedDealId);

      if (typeof reload === 'function') {
        reload();
      }
    } catch (err) {
      toastError(err);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleEditFileLink = async linkId => {
    if (editData.fileName.trim().length < 2) {
      toast.error(i18n.t('fileLinksModal.toasts.fileNameTooShort'));
      return;
    }

    if (!editData.fileUrl.trim()) {
      toast.error(i18n.t('fileLinksModal.toasts.fileUrlRequired'));
      return;
    }

    try {
      // Enviar apenas os campos que foram alterados
      const updateData = {};
      const originalLink = fileLinks.find(link => link.id === linkId);

      if (originalLink) {
        if (
          editData.fileName.trim() !==
          (originalLink.name || originalLink.fileName)
        ) {
          updateData.name = editData.fileName.trim();
        }
        if (
          editData.fileUrl.trim() !== (originalLink.url || originalLink.fileUrl)
        ) {
          updateData.url = editData.fileUrl.trim();
        }
      }

      // Só fazer a requisição se houver mudanças
      if (Object.keys(updateData).length > 0) {
        if (!resolvedDealId) {
          toast.error(
            'Não foi possível localizar o negócio para atualizar o anexo.'
          );
          return;
        }

        await api.put(`/deals/${resolvedDealId}/files/${linkId}`, updateData);
        toast.success(i18n.t('fileLinksModal.toasts.linkUpdated'));
      } else {
        toast.info('Nenhuma alteração foi feita');
      }

      setEditingLink(null);
      setEditData({ fileName: '', fileUrl: '' });
      loadFileLinks(resolvedDealId);

      if (typeof reload === 'function') {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteFileLink = async () => {
    if (!linkToDelete) return;

    try {
      if (!resolvedDealId) {
        console.error('DealId não encontrado para deletar. Ticket:', ticket);
        toast.error('Não foi possível localizar o negócio para atualizar o anexo. Verifique se o ticket possui um negócio associado.');
        return;
      }

      await api.delete(`/deals/${resolvedDealId}/files/${linkToDelete.id}`);
      toast.success(i18n.t('fileLinksModal.toasts.linkDeleted'));

      setConfirmationOpen(false);
      setLinkToDelete(null);
      loadFileLinks(resolvedDealId);

      if (typeof reload === 'function') {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
  };

  const openDeleteConfirmation = link => {
    setLinkToDelete(link);
    setConfirmationOpen(true);
  };

  const startEditing = link => {
    setEditingLink(link.id);
    setEditData({
      fileName: link.name || link.fileName,
      fileUrl: link.url || link.fileUrl,
    });
  };

  const cancelEditing = () => {
    setEditingLink(null);
    setEditData({ fileName: '', fileUrl: '' });
  };

  const openExternalLink = url => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderEditableListItem = link => {
    const isEditing = editingLink === link.id;

    const handleFileNameChange = e => {
      setEditData(prev => ({ ...prev, fileName: e.target.value }));
    };

    const handleFileUrlChange = e => {
      setEditData(prev => ({ ...prev, fileUrl: e.target.value }));
    };

    const handleSave = () => {
      handleEditFileLink(link.id);
    };

    if (isEditing) {
      return (
        <ListItem key={link.id} className={classes.listItem}>
          <Box className={classes.editFields}>
            <TextField
              value={editData.fileName}
              onChange={handleFileNameChange}
              label={i18n.t('fileLinksModal.form.fileName')}
              variant='outlined'
              size='small'
              fullWidth
            />
            <TextField
              value={editData.fileUrl}
              onChange={handleFileUrlChange}
              label={i18n.t('fileLinksModal.form.fileUrl')}
              variant='outlined'
              size='small'
              fullWidth
            />
            <Box display='flex' flexDirection='column'>
              <IconButton
                onClick={handleSave}
                color='primary'
                size='small'
                title={i18n.t('fileLinksModal.buttons.save')}
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                onClick={cancelEditing}
                color='secondary'
                size='small'
                title={i18n.t('fileLinksModal.buttons.cancel')}
              >
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
        </ListItem>
      );
    }

    return (
      <ListItem key={link.id} className={classes.listItem}>
        <LinkIcon color='primary' style={{ marginRight: 12, flexShrink: 0 }} />
        <ListItemText
          primary={
            <Box display='flex' alignItems='center' gap={1}>
              <Typography variant='subtitle1' component='span'>
                {link.name || link.fileName}
              </Typography>
              <IconButton
                size='small'
                onClick={() => openExternalLink(link.url || link.fileUrl)}
                color='primary'
                title={i18n.t('fileLinksModal.buttons.openLink')}
              >
                <LaunchIcon fontSize='small' />
              </IconButton>
            </Box>
          }
          secondary={
            <Typography className={classes.linkPreview}>
              {link.url || link.fileUrl}
            </Typography>
          }
        />
        <ListItemSecondaryAction>
          <IconButton
            onClick={() => startEditing(link)}
            color='primary'
            size='small'
            title={i18n.t('fileLinksModal.buttons.edit')}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => openDeleteConfirmation(link)}
            color='secondary'
            size='small'
            title={i18n.t('fileLinksModal.buttons.delete')}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t('fileLinksModal.confirmationModal.title')}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={handleDeleteFileLink}
      >
        {linkToDelete && (
          <>
            {i18n.t('fileLinksModal.confirmationModal.message', {
              fileName: linkToDelete.name || linkToDelete.fileName,
            })}
            <br />
            <strong>
              {i18n.t('fileLinksModal.confirmationModal.url')}:
            </strong>{' '}
            {linkToDelete.url || linkToDelete.fileUrl}
            <br />
            <br />
            {i18n.t('fileLinksModal.confirmationModal.warning')}
          </>
        )}
      </ConfirmationModal>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='lg'
        fullWidth
        scroll='paper'
      >
        <DialogTitle>
          <Box display='flex' alignItems='center'>
            <LinkIcon style={{ marginRight: 8 }} />
            {i18n.t('fileLinksModal.title')}
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Formulário para adicionar novo link */}
          <Box className={classes.formContainer}>
            <Typography variant='h6' gutterBottom>
              <AddIcon style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {i18n.t('fileLinksModal.addForm.title')}
            </Typography>

            <Formik
              initialValues={initialFormState}
              validationSchema={getFileLinkSchema()}
              onSubmit={handleAddFileLink}
              enableReinitialize
            >
              {({ touched, errors, isSubmitting }) => (
                <Form className={classes.addForm}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        name='fileName'
                        label={i18n.t('fileLinksModal.form.fileName')}
                        variant='outlined'
                        fullWidth
                        error={touched.fileName && Boolean(errors.fileName)}
                        helperText={touched.fileName && errors.fileName}
                        placeholder={i18n.t(
                          'fileLinksModal.form.fileNamePlaceholder'
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        name='fileUrl'
                        label={i18n.t('fileLinksModal.form.fileUrl')}
                        variant='outlined'
                        fullWidth
                        error={touched.fileUrl && Boolean(errors.fileUrl)}
                        helperText={touched.fileUrl && errors.fileUrl}
                        placeholder={i18n.t(
                          'fileLinksModal.form.fileUrlPlaceholder'
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Box mt={2}>
                    <Button
                      type='submit'
                      variant='contained'
                      color='primary'
                      disabled={isSubmitting}
                      className={classes.btnWrapper}
                      startIcon={<AddIcon />}
                    >
                      {i18n.t('fileLinksModal.buttons.addLink')}
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>

          <Divider />

          {/* Lista de links de arquivos */}
          <Box mt={2}>
            <Typography variant='h6' gutterBottom>
              {i18n.t('fileLinksModal.list.title', {
                count: Array.isArray(fileLinks) ? fileLinks.length : 0,
              })}
            </Typography>

            {loading ? (
              <Box display='flex' justifyContent='center' p={2}>
                <CircularProgress />
              </Box>
            ) : !Array.isArray(fileLinks) || fileLinks.length === 0 ? (
              <div className={classes.emptyState}>
                <LinkIcon style={{ fontSize: 48, color: 'inherit' }} />
                <Typography variant='body1' gutterBottom>
                  {i18n.t('fileLinksModal.emptyState.title')}
                </Typography>
                <Typography variant='body2'>
                  {i18n.t('fileLinksModal.emptyState.subtitle')}
                </Typography>
              </div>
            ) : (
              <List className={classes.fileList}>
                {Array.isArray(fileLinks) &&
                  fileLinks.map(link => renderEditableListItem(link))}
              </List>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color='secondary' variant='outlined'>
            {i18n.t('fileLinksModal.buttons.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FileLinksModal;
